import { CUSTOM_VALUE } from '@src/components/MarketSelector/tokens';
import {
  AggregationIcon,
  BaseSelect,
  EMPTY_ARRAY,
  FormControlSizes,
  HStack,
  useCallbackRef,
  useMarketAccountsContext,
  useMarketsContext,
  type BoxProps,
  type Security,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMarketSelectorBuyingPowerEnrichment } from '../../../../components/MarketSelector/enrichments/useMarketSelectorBuyingPowerEnrichment';
import { useUnsettledEnrichment } from '../../../../components/MarketSelector/enrichments/useUnsettledEnrichment';
import { useMarketDataMarketSelectorItems } from '../../../../components/MarketSelector/MarketData/useMarketDataMarketSelectorItems';
import {
  MarketSelectorContextProvider,
  useMarketSelectorContext,
} from '../../../../components/MarketSelector/MarketSelectorContext';
import {
  MarketSelectorPanel,
  type MarketSelectorPanelProps,
} from '../../../../components/MarketSelector/MarketSelectorPanel';
import { NarrowMarketSelectorPanel } from '../../../../components/MarketSelector/NarrowMarketSelectorPanel';
import { useMarketSelectorAggregationPresets } from '../../../../components/MarketSelector/presets/useMarketSelectorAggregationPresets';
import type { MarketSelectorValue, UseMarketSelectorPanelParams } from '../../../../components/MarketSelector/types';
import { useMarketSelector } from '../../../../components/MarketSelector/useMarketSelector';
import { useMarketSelectorPanel } from '../../../../components/MarketSelector/useMarketSelectorPanel';
import { useDeepDiveContext } from '../../../../providers/DeepDiveProvider';

interface DeepDiveMarketSelectorProps extends Omit<BoxProps, 'security'> {
  security: Security;
  isNarrow?: boolean;
  panelOffset?: number;
  panelMaxWidth?: string;
  size?: FormControlSizes;
}

export function DeepDiveMarketSelector({
  security,
  isNarrow = false,
  panelOffset,
  panelMaxWidth,
  size = FormControlSizes.Small,
  ...boxProps
}: DeepDiveMarketSelectorProps): JSX.Element {
  const marketSelectorItems = useMarketDataMarketSelectorItems({
    symbol: security?.Symbol,
  });

  const unsettledEnrichment = useUnsettledEnrichment({ security, marketSelectorItems });

  const buyingPowerEnrichment = useMarketSelectorBuyingPowerEnrichment({
    symbol: security?.Symbol ?? '',
    currency: security?.BaseCurrency ?? '',
    threshold: '10s',
    quantity: undefined,
    tag: 'DeepDiveMarketSelector',
    side: undefined,
    simpleModeVisibleColIDs: EMPTY_ARRAY,
  });

  const enrichmentSpecs = useMemo(
    () => compact([unsettledEnrichment, buyingPowerEnrichment]),
    [unsettledEnrichment, buyingPowerEnrichment]
  );

  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { selectedCombined, selectedAggregation, setMarketSelection } = useDeepDiveContext();

  const handleNewMarketSelectorChange = useCallbackRef((value: MarketSelectorValue) => {
    const selections = value.selections;
    const markets = selections.filter(mktOrAccName => marketsByName.has(mktOrAccName));
    const marketAccounts = selections.filter(mktOrAccName => marketAccountsByName.has(mktOrAccName));
    const aggregation = value.type === 'preset' ? value.presetID : undefined;

    setMarketSelection({ markets, marketAccounts, aggregation });
  });

  const newMarketSelectorValue: MarketSelectorValue = useMemo(() => {
    return selectedAggregation
      ? { type: 'preset', presetID: selectedAggregation, selections: selectedCombined }
      : { type: 'custom', selections: selectedCombined };
  }, [selectedCombined, selectedAggregation]);

  const { aggregationPresets } = useMarketSelectorAggregationPresets();

  const marketSelectorAPI = useMarketSelector({
    value: newMarketSelectorValue,
    onChange: handleNewMarketSelectorChange,
    items: marketSelectorItems,
    presets: aggregationPresets,
    enrichmentSpecs,
  });

  return (
    <MarketSelectorContextProvider marketSelectorAPI={marketSelectorAPI} isNarrow={isNarrow} showCredentialsOnlyToggle>
      <DeepDiveMarketSelectorInner
        size={size}
        {...boxProps}
        isNarrow={isNarrow}
        panelOffset={panelOffset}
        panelMaxWidth={panelMaxWidth}
      />
    </MarketSelectorContextProvider>
  );
}

function DeepDiveMarketSelectorInner({
  size,
  isNarrow,
  panelOffset = 0,
  panelMaxWidth,
  ...boxProps
}: { size?: FormControlSizes; isNarrow: boolean; panelOffset?: number; panelMaxWidth?: string } & BoxProps) {
  const { presetsByID, preset, selections } = useMarketSelectorContext();

  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleSelectClick = useCallback(() => {
    setIsOpen(open => !open);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const getLabel = useCallback(
    (value: string) => {
      if (value === CUSTOM_VALUE) {
        return 'Custom';
      }

      return presetsByID?.get(value)?.label ?? value;
    },
    [presetsByID]
  );

  const selectorValue = selections.length === 0 ? undefined : preset ? preset : CUSTOM_VALUE;

  return (
    <HStack ref={panelRef} gap="spacing4" minWidth="200px" {...boxProps} data-testid="market-selector">
      <BaseSelect
        value={selectorValue}
        getLabel={getLabel}
        prefix={<AggregationIcon />}
        isDropdownOpened={isOpen}
        onClick={handleSelectClick}
        placeholder="Select aggregation..."
        size={size}
        dataTestID="market-selector-main-select-button"
      />
      <MarketSelectorPanelStandalone
        isNarrow={isNarrow}
        panelMaxWidth={panelMaxWidth}
        isOpen={isOpen}
        close={handleClose}
        panelOffset={panelOffset}
        panelAnchorRef={panelRef}
        panelPlacement="bottom-start"
        testID="market-selector-panel"
      />
    </HStack>
  );
}

function MarketSelectorPanelStandalone({
  isNarrow,
  panelMaxWidth,
  isOpen,
  close,
  panelAnchorRef,
  panelOffset,
  panelPlacement,
  testID,
}: UseMarketSelectorPanelParams &
  Pick<MarketSelectorPanelProps, 'testID'> & { isNarrow: boolean; panelMaxWidth?: string }) {
  const dropdownProps = useMarketSelectorPanel({
    isOpen,
    close,
    panelAnchorRef,
    panelOffset,
    panelPlacement,
  });
  return isNarrow ? (
    <NarrowMarketSelectorPanel dropdownProps={dropdownProps} panelMaxWidth={panelMaxWidth} />
  ) : (
    <MarketSelectorPanel dropdownProps={dropdownProps} testID={testID} />
  );
}
